import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { ToastMessages } from "@/enums/ToastMessages";

// api ingang
let apiClient;
// functie om token uit authService te krijgen
//let getAuthToken;

const severityCode = {
  none: -1,
  hint: 0,
  warning: 1,
  error: 2,
  fatal: 3,
};

// -----------------------------------------------------------------
// Request cancellation
// -----------------------------------------------------------------
// Elke uitgaande request krijgt een eigen AbortController, die wordt
// bijgehouden in pendingRequests. Zo kunnen we op elk moment (bv. bij
// het verlaten van een pagina) alle nog openstaande requests afbreken.
// -----------------------------------------------------------------
let requestCounter = 0;
const pendingRequests = new Map();

/**
 * Breekt alle nog niet-afgeronde requests af.
 * Roep dit aan zodra de gebruiker de pagina/route verlaat.
 * @param {string} reason optioneel label, terug te vinden op de AbortSignal
 */
const cancelAllRequests = (reason = "Route changed / component unmounted") => {
  pendingRequests.forEach((controller) => controller.abort(reason));
  pendingRequests.clear();
};

/**
 * Breekt één specifieke request af, gegeven het requestId dat
 * getRequest/postRequest/etc. teruggeven.
 */
const cancelRequest = (requestId) => {
  const controller = pendingRequests.get(requestId);
  if (controller) {
    controller.abort();
    pendingRequests.delete(requestId);
  }
};

const isCancelledError = (error) => axios.isCancel(error) || error?.code === "ERR_CANCELED";

const getResponseStatus = (response, error) => response?.status ?? error?.response?.status;

const getNoUpdateResult = (response, error) => {
  const status = getResponseStatus(response, error);
  if (status !== 204 && status !== 304) {
    return undefined;
  }

  return {
    status: status,
    message: ToastMessages.DATA_NOT_MODIFIED,
    severity: severityCode.warning,
  };
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;

  if (typeof data === "string") {
    return data;
  }

  if (data && typeof data.message === "string") {
    return data.message;
  }

  if (error?.message) {
    return error.message;
  }

  return error?.toString?.() ?? "Request failed";
};

export default {
  install(Vue, options) {
    apiClient = axios.create({
      baseURL: `${options.portalUrl}api/`,
      timeout: 20000,
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    apiClient.interceptors.request.use(async (config) => {
      const authStore = useAuthStore();
      const token = await authStore.getAccessToken();
      config.headers["Authorization"] = `Bearer ${token}`;

      // AbortController koppelen aan deze request
      const controller = new AbortController();
      const requestId = ++requestCounter;
      config.signal = controller.signal;
      config.__requestId = requestId;
      pendingRequests.set(requestId, controller);

      return config;
    });

    // Zodra een request klaar is (succes of fout), opruimen uit de Map
    apiClient.interceptors.response.use(
      (response) => {
        pendingRequests.delete(response.config.__requestId);
        return response;
      },
      (error) => {
        if (error.config?.__requestId) {
          pendingRequests.delete(error.config.__requestId);
        }
        return Promise.reject(error);
      }
    );
  },
};

const getRequest = async (url, params) => {
  let message = undefined;
  let code = undefined;
  let severity = severityCode.none;
  let cancelled = false;

  let response = await apiClient.get(url, { params: params }).catch((error) => {
    if (isCancelledError(error)) {
      cancelled = true;
      return;
    }
    switch (error.response?.status) {
      case 404:
        router.push({ name: "Unauthorized" });
        break;
      default:
        message = getErrorMessage(error);
        severity = severityCode.error;
        code = error.response?.status;
    }
  });

  if (cancelled) {
    return { data: undefined, message: undefined, severity: severityCode.none, code: undefined, cancelled: true };
  }

  if (message == undefined) {
    const status = getResponseStatus(response);
    switch (status) {
      case 204: // no-content
        message = ToastMessages.DATA_NOT_MODIFIED;
        severity = severityCode.warning;
        code = status;
        break;
      case 290: // too many results
        message = "Teveel resultaten";
        severity = severityCode.warning;
        code = status;
        break;
    }
  }
  return { data: response ? response.data : undefined, message: message, severity: severity, code: code };
};

const postRequestBlob = async (url, data, params) => {
  let message = undefined;
  let filename = undefined;
  let severity = severityCode.none;
  let cancelled = false;

  const response = await apiClient
    .post(url, data, { params: params, timeout: 230000, responseType: "blob" })
    .then((response) => {
      var contentDisposition = response.headers["content-disposition"];
      filename = contentDisposition.match(/filename=(?<filename>[^,;]+);/)[1];
      return response;
    })
    .catch((error) => {
      if (isCancelledError(error)) {
        cancelled = true;
        return;
      }

      const noUpdateResult = getNoUpdateResult(undefined, error);
      if (noUpdateResult?.status === 304) {
        message = noUpdateResult.message;
        severity = noUpdateResult.severity;
        return;
      }

      if (error.response) {
        switch (error.response.status) {
          case 409:
            message = "conflict";
            break;
          default:
            message = getErrorMessage(error);
            break;
        }
      } else {
        message = getErrorMessage(error);
      }
      severity = severityCode.error;
    });

  if (cancelled) {
    return { data: undefined, filename: undefined, message: undefined, severity: severityCode.none, cancelled: true };
  }

  if (message == undefined) {
    const noUpdateResult = getNoUpdateResult(response);
    if (noUpdateResult) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
    }
  }
  return { data: response ? response.data : undefined, filename: filename, message: message, severity: severity };
};

const postRequest = async (url, data, params) => {
  let message = undefined;
  let code = undefined;
  let severity = severityCode.none;
  let cancelled = false;

  const response = await apiClient.post(url, data, { params: params, timeout: 90000 }).catch((error) => {
    if (isCancelledError(error)) {
      cancelled = true;
      return;
    }

    const noUpdateResult = getNoUpdateResult(undefined, error);
    if (noUpdateResult?.status === 304) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
      code = noUpdateResult.status;
      return;
    }

    const status = getResponseStatus(undefined, error);
    message = getErrorMessage(error);
    severity = severityCode.error;
    code = status;
    if (error.response) {
      switch (error.response?.status) {
        case 409:
          message = "conflict";
          break;
        default:
          message = error.response.data.message ? error.response.data.message : "Network exception";
      }
    }
  });

  if (cancelled) {
    return { data: undefined, message: undefined, severity: severityCode.none, code: undefined, cancelled: true };
  }

  if (message == undefined) {
    const noUpdateResult = getNoUpdateResult(response);
    if (noUpdateResult) {
      code = noUpdateResult.status;
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
    }
  }
  return { data: response ? response.data : undefined, message: message, severity: severity, code: code };
};

const putRequest = async (url, data, params) => {
  let message = undefined;
  let severity = severityCode.none;
  let cancelled = false;
  const response = await apiClient.put(url, data, { params: params }).catch((error) => {
    if (isCancelledError(error)) {
      cancelled = true;
      return;
    }

    const noUpdateResult = getNoUpdateResult(undefined, error);
    if (noUpdateResult?.status === 304) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
      return;
    }

    message = getErrorMessage(error);
    severity = severityCode.error;
  });

  if (cancelled) {
    return { data: undefined, message: undefined, severity: severityCode.none, cancelled: true };
  }

  if (message == undefined) {
    const noUpdateResult = getNoUpdateResult(response);
    if (noUpdateResult) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
    }
  }
  return { data: response ? response.data : undefined, message: message, severity: severity };
};

const patchRequest = async (url, data, params) => {
  let message = undefined;
  let severity = severityCode.none;
  let cancelled = false;

  const response = await apiClient.patch(url, data, { params: params }).catch((error) => {
    if (isCancelledError(error)) {
      cancelled = true;
      return;
    }

    const noUpdateResult = getNoUpdateResult(undefined, error);
    if (noUpdateResult?.status === 304) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
      return;
    }

    message = getErrorMessage(error);
    severity = severityCode.error;
  });

  if (cancelled) {
    return { data: undefined, message: undefined, severity: severityCode.none, cancelled: true };
  }

  if (message == undefined) {
    const noUpdateResult = getNoUpdateResult(response);
    if (noUpdateResult) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
    }
  }
  return { data: response ? response.data : undefined, message: message, severity: severity };
};

const deleteRequest = async (url) => {
  let message = undefined;
  let severity = severityCode.none;
  let cancelled = false;

  const response = await apiClient.delete(url).catch((error) => {
    if (isCancelledError(error)) {
      cancelled = true;
      return;
    }

    const noUpdateResult = getNoUpdateResult(undefined, error);
    if (noUpdateResult?.status === 304) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
      return;
    }

    message = getErrorMessage(error);
    severity = severityCode.error;
  });

  if (cancelled) {
    return { data: undefined, message: undefined, severity: severityCode.none, cancelled: true };
  }

  if (message == undefined) {
    const noUpdateResult = getNoUpdateResult(response);
    if (noUpdateResult) {
      message = noUpdateResult.message;
      severity = noUpdateResult.severity;
    }
  }
  return { data: response ? response.data : undefined, message: message, severity: severity };
};

export {
  apiClient,
  getRequest,
  postRequest,
  postRequestBlob,
  putRequest,
  patchRequest,
  deleteRequest,
  severityCode,
  cancelAllRequests,
  cancelRequest,
};